'use client';

import React, { useState } from 'react';
import { ListPlus, Plus, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { css } from 'styled-system/css';
import { z } from 'zod';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@shaojiang/components/ui/button';
import { Field } from '@shaojiang/components/ui/field';
import { useTodoStore } from '@shaojiang/store/todo';
import { TaskSchema } from '@shaojiang/types/todo';

const NewTaskSchema = z.object({
  content: TaskSchema.shape.content.trim().max(120, 'Keep it under 120 characters'),
});

type NewTask = z.infer<typeof NewTaskSchema>;

export default function AddTask() {
  const addTask = useTodoStore((state) => state.addTask);
  const [addingTask, setAddingTask] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NewTask>({ resolver: zodResolver(NewTaskSchema), defaultValues: { content: '' } });

  const onSubmit = ({ content }: NewTask) => {
    addTask(content);
    reset();
  };

  return (
    <div>
      {addingTask && (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={css({ display: 'flex', alignItems: 'flex-start', gap: '3', py: '1', px: '2' })}
        >
          <ListPlus className={css({ ml: '2', mt: '2', flexShrink: 0 })} />
          <Field.Root invalid={!!errors.content} flex='1'>
            <Field.Input placeholder='New task content' autoFocus {...register('content')} />
            {errors.content && <Field.ErrorText>{errors.content.message}</Field.ErrorText>}
          </Field.Root>
          <Button type='submit'>Save</Button>
        </form>
      )}
      <Button
        mt='6'
        variant={addingTask ? 'outline' : 'solid'}
        colorPalette={addingTask ? 'gray' : 'primary'}
        onClick={() => setAddingTask((adding) => !adding)}
      >
        {addingTask ? <X /> : <Plus />}
        {addingTask ? 'Cancel' : 'Add new task'}
      </Button>
    </div>
  );
}
